import { translate } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { copyToClipboard } from "@/lib/copy-to-clipboard";
import { toAgendaItems } from "@/components/shapes/agenda-frame/agenda-items";
import {
  docPlainText,
  isDocContent,
} from "@/components/shapes/doc-frame/doc-content";
import { todoItemsFrom } from "@/components/shapes/todo-frame/todo-items";
import {
  asRecord,
  asString,
  compact,
  recordsToText,
  type PlainRecord,
} from "./plain-record";
import type { FrameData } from "@/lib/frame-data";

function selectionBelongsTo(root: HTMLElement, selection: Selection): boolean {
  if (selection.anchorNode && !root.contains(selection.anchorNode))
    return false;
  if (selection.focusNode && !root.contains(selection.focusNode)) return false;
  for (let index = 0; index < selection.rangeCount; index += 1) {
    const range = selection.getRangeAt(index);
    if (!root.contains(range.commonAncestorContainer)) return false;
  }
  return selection.rangeCount > 0;
}

function selectedTextWithin(root: HTMLElement | null): string {
  if (!root || typeof window === "undefined") return "";
  const selection = window.getSelection();
  if (
    !selection ||
    selection.isCollapsed ||
    !selectionBelongsTo(root, selection)
  ) {
    return "";
  }
  const text = selection.toString();
  return text.trim() ? text : "";
}

function searchContentToText(content: PlainRecord): string {
  return recordsToText(content.results, (result) =>
    compact([
      asString(result.title),
      asString(result.url),
      asString(result.snippet) ||
        asString(result.description) ||
        asString(result.text),
    ]),
  );
}

function emailContentToText(content: PlainRecord): string {
  const to = asString(content.to);
  const subject = asString(content.subject);
  return compact([
    to
      ? translate(framesSharedMessages, "clipboardEmailTo", { value: to })
      : "",
    subject
      ? translate(framesSharedMessages, "clipboardEmailSubject", {
          value: subject,
        })
      : "",
    asString(content.body),
  ]);
}

function newsletterContentToText(content: PlainRecord): string {
  const sections = recordsToText(content.sections, (section) =>
    compact([asString(section.title), asString(section.body)]),
  );
  return compact([
    asString(content.subject),
    asString(content.previewText),
    sections,
  ]);
}

function imageContentToText(content: PlainRecord): string {
  return recordsToText(content.images, (image) =>
    compact([
      asString(image.caption),
      asString(image.alt),
      asString(image.url),
    ]),
  );
}

/** A checklist as Markdown task lines, so it pastes as one anywhere. */
function todoContentToText(content: PlainRecord): string {
  return todoItemsFrom(content.items)
    .map((item) => {
      const line = `- [${item.done ? "x" : " "}] ${item.text}`;
      return item.detail ? `${line}\n  ${item.detail}` : line;
    })
    .join("\n");
}

/** An agenda's or a calendar's day: its date, then a line per event. */
function eventsToText(date: string, events: unknown): string {
  const lines = (toAgendaItems(events) ?? []).map((event) =>
    [[event.time, event.title].filter(Boolean).join(" "), event.detail]
      .filter(Boolean)
      .join(" — "),
  );
  return compact([date, lines.join("\n")]);
}

/** A chat's or a Slack channel's messages, each after its sender. */
function messagesToText(content: PlainRecord): string {
  return recordsToText(content.messages, (message) => {
    const sender = asString(message.user) || asString(message.from);
    const text = asString(message.text);
    return sender && text ? `${sender}: ${text}` : text;
  });
}

function genericContentToText(content: PlainRecord): string {
  const input = asRecord(content.input);
  return compact([
    asString(content.markdown),
    asString(content.code),
    asString(content.html),
    asString(content.text),
    asString(content.body),
    asString(content.url),
    asString(content.command),
    input ? asString(input.command) || asString(input.query) : "",
  ]);
}

/** The frame types whose content has its own text; the rest read the generic fields. */
const CONTENT_TEXT: Partial<
  Record<FrameData["frameType"], (content: PlainRecord) => string>
> = {
  search: searchContentToText,
  email: emailContentToText,
  newsletter: newsletterContentToText,
  image: imageContentToText,
  todo: todoContentToText,
  agenda: (content) => eventsToText(asString(content.date), content.items),
  calendar: (content) => eventsToText("", content.events),
  chat: messagesToText,
  slack: messagesToText,
};

/** The text of the frame's content; empty when it has none (a title alone is not content). */
export function frameContentToClipboardText(frame: FrameData): string {
  const content = frame.content ?? {};
  const byType =
    frame.frameType === "note" ||
    frame.frameType === "doc" ||
    (frame.frameType !== "search" && isDocContent(content))
      ? docPlainText(frame.frameType, content)
      : (CONTENT_TEXT[frame.frameType] ?? genericContentToText)(content);

  return byType.trim();
}

/**
 * What Copy writes: the text selected inside `root` (or the selection kept
 * from when the menu opened), else a conversation's rendered thread, else
 * the frame's content text. Empty when there is nothing to copy.
 */
function frameCopyText(
  frame: FrameData,
  root: HTMLElement | null,
  preservedSelection: string,
): string {
  const selected = preservedSelection.trim() || selectedTextWithin(root);
  if (selected) return selected;
  const thread =
    frame.frameType === "conversation"
      ? (
          root?.querySelector<HTMLElement>("[data-frame-content='true']")
            ?.innerText ?? ""
        ).trim()
      : "";
  return thread || frameContentToClipboardText(frame);
}

/**
 * Whether Copy has text to write for a full frame; `root` is its rendered
 * root, if any. Shared with `copyFrameText`, so a menu offers Copy only when
 * it copies something.
 */
export function frameHasCopyText(
  frame: FrameData,
  root: HTMLElement | null,
  preservedSelection = "",
): boolean {
  return frameCopyText(frame, root, preservedSelection) !== "";
}

export function copyFrameText(
  frame: FrameData,
  root: HTMLElement | null,
  preservedSelection = "",
): Promise<boolean> {
  const text = frameCopyText(frame, root, preservedSelection);
  if (!text) return Promise.resolve(false);
  return copyToClipboard(text);
}
